// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Image, Modal, Platform, Pressable, ScrollView, Share, StyleSheet, View } from 'react-native';
import { Redirect, useLocalSearchParams, useRootNavigationState, useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import QRCode from 'react-native-qrcode-svg';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useUser } from './user-context';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { setCurrentRoundId } from '@/services/currentRound';
import { useI18n } from '@/i18n';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getTourImageUri } from '@/services/tourImageSource';
import { useCreateTour } from '@/hooks/useCreateTour';
import CreateTourModal from '@/components/CreateTourModal';
import { getAssetImageSource } from '@/services/assetImageSource';
import { resolveTourInviteLink } from '@/services/tourInviteLink';
import appColors from '@/theme/colors';
import type { TourRoundSummary } from '@/features/round-init/types';
import UpcomingRoundsSection from '@/features/round-scheduling/UpcomingRoundsSection';

// Order a tour's rounds by playing date (earliest first), falling back to creation time.
const roundPlayDateOrder = (round: { playDate?: string; createdAt?: string }) => {
  const candidate = round.playDate ?? round.createdAt;
  if (!candidate) return 0;
  const parsed = Date.parse(candidate);
  return Number.isFinite(parsed) ? parsed : 0;
};

type TourSummary = {
  id: string;
  tourName?: string;
  status?: string;
  isHidden?: boolean;
  playerNames?: string[];
  createdAt?: string;
  imageKey?: string | null;
  imageThumbKey?: string | null;
};

type TourRoundsState = {
  loading: boolean;
  error: string | null;
  rounds: TourRoundSummary[];
};

export default function MyToursScreen() {
  const { debug: debugParam } = useLocalSearchParams<{ debug?: string }>();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { user, clear } = useUser();
  const { t } = useI18n();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const trophyImage = useMemo(() => getAssetImageSource('images/trophy.png', 'icon', '/my-tours'), []);
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const [tours, setTours] = useState<TourSummary[]>([]);
  const [statusNote, setStatusNote] = useState<string>(t('tournaments.loading'));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [openTourMenuId, setOpenTourMenuId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showArchivedSingleRounds, setShowArchivedSingleRounds] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalTourName, setShareModalTourName] = useState<string | null>(null);
  const [shareModalLink, setShareModalLink] = useState<string | null>(null);
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [shareModalError, setShareModalError] = useState<string | null>(null);
  const [deleteConfirmRound, setDeleteConfirmRound] = useState<{ tourId: string; name: string } | null>(null);
  const [openSingleRoundMenuKey, setOpenSingleRoundMenuKey] = useState<string | null>(null);
  const [finishConfirmRound, setFinishConfirmRound] = useState<{ roundId: string; name: string } | null>(null);
  const [roundsByTourId, setRoundsByTourId] = useState<Record<string, TourRoundsState>>({});
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const pendingStatusRequestIdRef = useRef<string | null>(null);
  const currentPlayerIdRef = useRef<string | null>(null);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'my-tours',
      }),
    [apiBaseUrl, user?.token]
  );

  const { createTour, creating: tourStarting, error: createError } = useCreateTour({
    onCreated: (tourId) => {
      setCreateModalOpen(false);
      router.push(`/tour/${tourId}/edit?created=1${debug ? '&debug=true' : ''}`);
    },
  });

  const visibleTours = useMemo(() => {
    const nonHiddenTours = tours.filter((tour) => !tour.isHidden);
    if (showArchived) return nonHiddenTours;
    return nonHiddenTours.filter((tour) => (tour.status ?? '').toLowerCase() !== 'archived');
  }, [showArchived, tours]);
  const myToursAndRoundsSummary = useMemo(() => {
    const tourCount = visibleTours.length;
    const unfinishedRoundCount = visibleTours.reduce((total, tour) => {
      const rounds = roundsByTourId[tour.id]?.rounds ?? [];
      const unfinishedRounds = rounds.filter((round) => {
        const status = (round.status ?? '').toLowerCase();
        return status !== 'cancelled' && status !== 'finished';
      });
      return total + unfinishedRounds.length;
    }, 0);
    if (tourCount === 0 && unfinishedRoundCount === 0) return null;
    return t('tournaments.myToursAndRoundsSummary', { tours: tourCount, rounds: unfinishedRoundCount });
  }, [roundsByTourId, t, visibleTours]);
  const singleRoundRows = useMemo(() => {
    const rows: Array<{ tourId: string; tourName: string; playerLine: string; round: TourRoundSummary }> = [];
    tours.forEach((tour) => {
      if (!tour.isHidden) {
        return;
      }
      if (!showArchivedSingleRounds && (tour.status ?? '').toLowerCase() === 'archived') {
        return;
      }
      // A single round's players are the players of its hidden one-round tour.
      const playerLine = (tour.playerNames ?? []).map((name) => name.trim()).filter(Boolean).join(', ');
      const rounds = roundsByTourId[tour.id]?.rounds ?? [];
      rounds.forEach((round) => {
        const status = (round.status ?? '').toLowerCase();
        if (status === 'cancelled') {
          return;
        }
        if (!showArchivedSingleRounds && status === 'finished') {
          return;
        }
        rows.push({
          tourId: tour.id,
          tourName: tour.tourName ?? t('tournaments.defaultName'),
          playerLine,
          round,
        });
      });
    });
    return rows.sort((a, b) => {
      const aTime = Date.parse(a.round.statusUpdatedAt ?? a.round.createdAt ?? '') || 0;
      const bTime = Date.parse(b.round.statusUpdatedAt ?? b.round.createdAt ?? '') || 0;
      return bTime - aTime;
    });
  }, [roundsByTourId, showArchivedSingleRounds, t, tours]);

  const formatRoundStatus = useCallback(
    (round: TourRoundSummary) => {
      if (!round.status) return null;
      const normalized = round.status.toLowerCase();
      if (normalized === 'active' || normalized === 'finished') {
        const timestamp = round.statusUpdatedAt ?? round.createdAt;
        if (!timestamp) {
          return t('tournaments.roundStatus', { status: round.status });
        }
        const dateLabel = new Date(timestamp).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
        return t('tournaments.roundStatusWithDate', { status: round.status, date: dateLabel });
      }
      return t('tournaments.roundStatus', { status: round.status });
    },
    [t]
  );

  const fetchRoundsForTour = useCallback(
    async (tourId: string) => {
      if (!user?.token) return;
      setRoundsByTourId((prev) => ({
        ...prev,
        [tourId]: {
          loading: true,
          error: null,
          rounds: prev[tourId]?.rounds ?? [],
        },
      }));
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/tour/${tourId}?status=draft,open,active,playing,started,finished`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to load rounds (${response.status})`);
        }
        const data = (await response.json()) as TourRoundSummary[];
        setRoundsByTourId((prev) => ({
          ...prev,
          [tourId]: {
            loading: false,
            error: null,
            rounds: data,
          },
        }));
      } catch {
        setRoundsByTourId((prev) => ({
          ...prev,
          [tourId]: {
            loading: false,
            error: t('tournaments.roundsLoadError'),
            rounds: [],
          },
        }));
      }
    },
    [apiBaseUrl, t, user?.token]
  );

  const fetchTours = useCallback(async (playerIdOverride?: string) => {
    if (!user?.token) {
      clear();
      router.replace('/');
      return;
    }
    if (!user?.id && !playerIdOverride) return;
    setLoading(true);
    setError(null);
    try {
      let playerId = playerIdOverride ?? currentPlayerIdRef.current;
      if (!playerId && user?.id) {
        const playerResp = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
          headers: user.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (playerResp.status === 401 || playerResp.status === 403) {
          clear();
          router.replace('/');
          return;
        }
        if (playerResp.status === 404) {
          setTours([]);
          setStatusNote(t('tournaments.playerProvisioning'));
          return;
        }
        if (!playerResp.ok) throw new Error(await playerResp.text());
        const player = await playerResp.json() as { id?: string };
        playerId = player.id ?? null;
      }
      if (!playerId) {
        setTours([]);
        setStatusNote(t('tournaments.playerProvisioning'));
        return;
      }

      currentPlayerIdRef.current = playerId;
      const response = await fetch(`${apiBaseUrl}/api/tours?playerId=${playerId}&includeHidden=true`, {
        headers: user.token ? { Authorization: `Bearer ${user.token}` } : undefined,
      });
      if (!response.ok) throw new Error(await response.text());
      const data = (await response.json()) as Array<{
        id: string;
        tourName?: string;
        TourName?: string;
        status?: string;
        Status?: string;
        isHidden?: boolean;
        IsHidden?: boolean;
        playerNames?: string[];
        PlayerNames?: string[];
        createdAt?: string;
        CreatedAt?: string;
        imageKey?: string | null;
        ImageKey?: string | null;
        imageThumbKey?: string | null;
        ImageThumbKey?: string | null;
      }>;
      setTours(data.map((t) => ({
        id: t.id,
        tourName: t.tourName ?? t.TourName,
        status: t.status ?? t.Status,
        isHidden: t.isHidden ?? t.IsHidden ?? false,
        playerNames: t.playerNames ?? t.PlayerNames,
        createdAt: t.createdAt ?? t.CreatedAt,
        imageKey: t.imageKey ?? t.ImageKey ?? null,
        imageThumbKey: t.imageThumbKey ?? t.ImageThumbKey ?? null,
      })));
      setStatusNote(data.length ? '' : t('tournaments.empty'));
      if (data.length === 0) {
        setCurrentTourId(null);
      }
    } catch (err) {
      setError(t('tournaments.loadError'));
      setTours([]);
      setStatusNote('');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, user?.id, user?.token, clear, router, t]);

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    if (isFocused) {
      socket.setSubscriptionContext({ roundId: null, tourId: null });
    }
    const unsubscribe = socket.onMessage((message) => {
      const messageType = (message as { type?: string }).type;
      if (messageType === 'tour.update.ack') {
        pendingStatusRequestIdRef.current = null;
        setStatusNote(t('tournaments.statusUpdateAck'));
        return;
      }
      if (messageType === 'tour.update.rejected') {
        const payload = message as { requestId?: string; messageKey?: string; message?: string };
        if (pendingStatusRequestIdRef.current && payload.requestId && payload.requestId !== pendingStatusRequestIdRef.current) return;
        pendingStatusRequestIdRef.current = null;
        const translated = payload.messageKey ? t(payload.messageKey) : '';
        setError(
          translated && payload.messageKey && translated !== payload.messageKey
            ? translated
            : payload.message || t('tournaments.statusUpdateFailed')
        );
        setStatusNote('');
        // The status change was optimistically applied (archived/cancelled tours
        // are dropped from the list), so re-read the real state.
        fetchTours();
        return;
      }
      // useCreateTour owns this screen's own creation (including navigation);
      // refreshing the list covers both that and tours created elsewhere.
      if (messageType === 'tour.created') {
        fetchTours();
      }
    });
    const unsubscribeError = socket.onError(() => null);
    return () => {
      unsubscribe();
      unsubscribeError();
    };
  }, [fetchTours, isFocused, user?.token, t]);

  useEffect(() => {
    if (!isNavigationReady) return;
    if (!user?.token) {
      clear();
      router.replace('/');
      return;
    }
    if (!user?.id) return;
    void fetchTours();
  }, [clear, fetchTours, isNavigationReady, router, user?.id, user?.token]);

  useEffect(() => {
    if (!isFocused || !isNavigationReady || !user?.token) return;
    tours.forEach((tour) => {
      void fetchRoundsForTour(tour.id);
    });
  }, [fetchRoundsForTour, isFocused, isNavigationReady, tours, user?.token]);

  const updateTourStatus = (tourId: string, status: 'paused' | 'archived' | 'cancelled' | 'finished' | 'active') => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const requestId = generateId();
    pendingStatusRequestIdRef.current = requestId;
    const sent = socket.send({
      type: 'tour.update',
      tourId,
      requestId,
      status,
    });
    if (sent !== 'sent') {
      setStatusNote(t('tournaments.statusUpdateQueued'));
    } else {
      setStatusNote(t('tournaments.statusUpdateSent', { status }));
    }
    logToBff('tournaments.status.selected', { tourId, status, requestId });
    if (status === 'archived') {
      console.info('[tournaments] archive', { tourId, status });
    }
    setOpenTourMenuId(null);
    setTours((prev) => {
      if (status === 'archived' || status === 'cancelled') {
        return prev.filter((t) => t.id !== tourId);
      }
      return prev.map((t) => (t.id === tourId ? { ...t, status } : t));
    });
  };

  // Single rounds live in a hidden one-round tour, so finishing one is a round-level
  // status change rather than the tour.update the tour cards send.
  const finishRound = (roundId: string) => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const sent = socket.send({ type: 'round.status.update', roundId, status: 'finished' });
    if (sent !== 'sent') {
      setStatusNote(t('tournaments.statusUpdateQueued'));
    }
    logToBff('tournaments.round.finish', { roundId });
    setOpenSingleRoundMenuKey(null);
    setRoundsByTourId((prev) => {
      const next: Record<string, TourRoundsState> = {};
      Object.entries(prev).forEach(([tourId, state]) => {
        next[tourId] = {
          ...state,
          rounds: state.rounds.map((round) => (round.id === roundId ? { ...round, status: 'finished' } : round)),
        };
      });
      return next;
    });
  };

  const resolveInviteLink = async (tourId: string) => {
    return resolveTourInviteLink({
      apiBaseUrl,
      tourId,
      authToken: user?.token,
    });
  };

  const handleOpenShareModal = async (tour: TourSummary) => {
    setShareModalLoading(true);
    setShareModalError(null);
    setShareModalTourName(tour.tourName ?? t('tournaments.defaultName'));
    setShareModalLink(null);
    setOpenTourMenuId(null);

    try {
      const inviteLink = await resolveInviteLink(tour.id);
      setShareModalLink(inviteLink);
      setShareModalOpen(true);
    } catch (shareError) {
      setShareModalError(t('tournaments.shareLoadError'));
      setStatusNote(t('tournaments.shareLoadError'));
      logToBff('tournaments.share.link.error', {
        tourId: tour.id,
        message: shareError instanceof Error ? shareError.message : String(shareError),
      }, 'warn');
    } finally {
      setShareModalLoading(false);
    }
  };

  const handleShareInviteLink = async () => {
    if (!shareModalLink) {
      return;
    }

    const title = t('tournaments.shareTitle', { name: shareModalTourName ?? t('tournaments.defaultName') });
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
        const navWithShare = navigator as {
          share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
          clipboard?: { writeText?: (value: string) => Promise<void> };
        };

        if (typeof navWithShare.share === 'function') {
          await navWithShare.share({
            title,
            url: shareModalLink,
          });
          return;
        }

        if (navWithShare.clipboard?.writeText) {
          await navWithShare.clipboard.writeText(shareModalLink);
          setStatusNote(t('tournaments.shareCopied'));
          return;
        }

        throw new Error('share unsupported');
      }

      await Share.share({
        title,
        message: shareModalLink,
      });
    } catch (shareError) {
      const message = shareError instanceof Error ? shareError.message : String(shareError);
      const canceled = message.toLowerCase().includes('cancel');
      if (!canceled) {
        setShareModalError(t('tournaments.shareSendError'));
      }
    }
  };

  const shareJoinCode = useMemo(() => {
    if (!shareModalLink) {
      return null;
    }

    const token = decodeURIComponent(shareModalLink.split('/').pop() ?? '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .trim()
      .toUpperCase();

    if (!token) {
      return null;
    }

    return token.length > 6 ? token.slice(0, 6) : token;
  }, [shareModalLink]);

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/" />;

  const openRoundFromTour = (tourId: string, round: TourRoundSummary) => {
    setCurrentTourId(tourId);
    setCurrentRoundId(round.id);
    const debugSuffix = debug ? '?debug=true' : '';
    const normalizedRoundStatus = (round.status ?? '').toLowerCase();
    const roundPath = normalizedRoundStatus === 'finished'
      ? `/round/${round.id}/summary${debugSuffix}`
      : normalizedRoundStatus === 'draft'
        ? `/round/${round.id}/roundstart${debugSuffix}`
        : `/round/${round.id}/hole/1/compact${debugSuffix}`;
    router.push(roundPath);
  };

  return (
    <>
      <ScrollView
        contentContainerStyle={styles.container}
        onScrollBeginDrag={() => {
          setOpenTourMenuId(null);
          setOpenSingleRoundMenuKey(null);
        }}
      >
        {debug && user ? (
          <Text style={styles.debugUser}>User: {user.forename ?? user.id} ({user.id})</Text>
        ) : null}
        <Image source={trophyImage} style={styles.heroImage} resizeMode="contain" />
        <Text style={styles.title}>{t('welcome.myTours')}</Text>
          <Text style={styles.meta}>{statusNote}</Text>
          {myToursAndRoundsSummary ? <Text style={styles.meta}>{myToursAndRoundsSummary}</Text> : null}
          <UpcomingRoundsSection userId={user?.id} token={user?.token} />
          <Pressable
            style={[styles.toggleButton, showArchivedSingleRounds && styles.toggleButtonActive]}
            onPress={() => setShowArchivedSingleRounds((prev) => !prev)}
            accessibilityRole="switch"
            accessibilityState={{ checked: showArchivedSingleRounds }}
          >
            <View style={styles.toggleIndicator}>
              <View style={[styles.toggleDot, showArchivedSingleRounds && styles.toggleDotActive]} />
            </View>
            <Text style={styles.toggleButtonText}>
              {showArchivedSingleRounds ? t('tournaments.hideFinishedSingleRounds') : t('tournaments.showFinishedSingleRounds')}
            </Text>
          </Pressable>
          {singleRoundRows.length > 0 ? (
            <View style={[styles.roundsBlock, !!openSingleRoundMenuKey && styles.roundsBlockMenuOpen]}>
              <Text style={styles.roundsTitle}>
                {t('tournaments.hiddenToursSingleRoundsTitle')} ({singleRoundRows.length})
              </Text>
              <View style={styles.roundsGrid}>
                {singleRoundRows.map((row) => {
                  const roundName = row.round.name ?? row.round.courseName ?? t('tournaments.roundFallbackName');
                  const roundStatusLabel = formatRoundStatus(row.round);
                  const normalizedRoundStatus = (row.round.status ?? '').toLowerCase();
                  const isFinished = normalizedRoundStatus === 'finished';
                  const isDraft = normalizedRoundStatus === 'draft';
                  const roundActionLabel = isFinished
                    ? t('tournaments.roundResults')
                    : isDraft
                      ? t('tournaments.roundStart')
                      : t('tournaments.roundOpen');
                  const menuKey = `${row.tourId}:${row.round.id}`;
                  const roundMenuOpen = openSingleRoundMenuKey === menuKey;
                  const debugSuffix = debug ? '&debug=true' : '';
                  return (
                    <View key={menuKey} style={[styles.roundRow, roundMenuOpen && styles.roundRowMenuOpen]}>
                      <View style={styles.roundCard}>
                        <View style={styles.roundHeaderRow}>
                          <View style={styles.roundMenuAnchor}>
                            <Pressable
                              style={styles.roundMenuButton}
                              onPress={() => {
                                setOpenTourMenuId(null);
                                setOpenSingleRoundMenuKey(roundMenuOpen ? null : menuKey);
                              }}
                              accessibilityLabel={t('tournaments.roundMenuLabel')}
                            >
                              <Text style={styles.tourMenuIcon}>⋯</Text>
                            </Pressable>
                            {roundMenuOpen ? (
                              <View style={styles.roundMenu}>
                                <Pressable
                                  style={styles.tourMenuItem}
                                  onPress={() => {
                                    setOpenSingleRoundMenuKey(null);
                                    setCurrentTourId(row.tourId);
                                    setCurrentRoundId(row.round.id);
                                    router.push(`/round/${row.round.id}/roundstart?mode=update${debugSuffix}`);
                                  }}
                                >
                                  <Text style={styles.tourMenuText}>{t('tournaments.roundSettings')}</Text>
                                </Pressable>
                                {/* The only way back to a single round's photos. Its gallery lives
                                    under the hidden tour that backs the round, which nothing else
                                    here links to — without this the photos are unreachable once the
                                    round is over. Scoped to the round so it opens on its photos. */}
                                <Pressable
                                  style={styles.tourMenuItem}
                                  onPress={() => {
                                    setOpenSingleRoundMenuKey(null);
                                    setCurrentTourId(row.tourId);
                                    setCurrentRoundId(row.round.id);
                                    router.push(
                                      `/tour/${row.tourId}/photos?roundId=${row.round.id}`
                                    );
                                  }}
                                >
                                  <Text style={styles.tourMenuText}>{t('tourPhotos.openAction')}</Text>
                                </Pressable>
                                <Pressable
                                  style={styles.tourMenuItem}
                                  onPress={() => {
                                    setOpenSingleRoundMenuKey(null);
                                    void handleOpenShareModal({ id: row.tourId, tourName: row.tourName });
                                  }}
                                >
                                  <Text style={styles.tourMenuText}>{t('tournaments.shareCta')}</Text>
                                </Pressable>
                                {!isFinished ? (
                                  <Pressable
                                    style={styles.tourMenuItem}
                                    onPress={() => {
                                      setOpenSingleRoundMenuKey(null);
                                      setFinishConfirmRound({ roundId: row.round.id, name: roundName });
                                    }}
                                  >
                                    <Text style={styles.tourMenuText}>{t('tournaments.statusFinishRound')}</Text>
                                  </Pressable>
                                ) : null}
                                <Pressable
                                  style={styles.tourMenuItem}
                                  onPress={() => {
                                    setOpenSingleRoundMenuKey(null);
                                    updateTourStatus(row.tourId, 'archived');
                                  }}
                                >
                                  <Text style={styles.tourMenuText}>{t('tournaments.statusArchive')}</Text>
                                </Pressable>
                                <Pressable
                                  style={styles.tourMenuItem}
                                  onPress={() => {
                                    setOpenSingleRoundMenuKey(null);
                                    setDeleteConfirmRound({ tourId: row.tourId, name: roundName });
                                  }}
                                >
                                  <Text style={[styles.tourMenuText, styles.tourMenuTextDanger]}>{t('tournaments.statusDelete')}</Text>
                                </Pressable>
                              </View>
                            ) : null}
                          </View>
                          <View style={styles.roundHeader}>
                            <Text style={styles.roundName}>{roundName}</Text>
                            <Text style={styles.meta}>{row.tourName}</Text>
                            {row.playerLine ? <Text style={styles.roundMeta}>{row.playerLine}</Text> : null}
                            {roundStatusLabel ? <Text style={styles.roundMeta}>{roundStatusLabel}</Text> : null}
                          </View>
                          <GoffyButton
                            label={roundActionLabel}
                            onPress={() => openRoundFromTour(row.tourId, row.round)}
                            size="compact"
                            tone="dark"
                            oldButtonStyle={styles.roundActionButton}
                            oldDisabledStyle={styles.primaryButtonDisabled}
                            style={styles.hiddenRoundActionButtonWrap}
                            textStyle={styles.roundActionButtonText}
                          />
                        </View>
                      </View>
                    </View>
                  );
                })}
              </View>
            </View>
          ) : null}
          {loading ? <Text style={styles.meta}>{t('tournaments.loading')}</Text> : null}
          {error ? <Text style={styles.error}>{error}</Text> : null}
        <Pressable
          style={[styles.toggleButton, showArchived && styles.toggleButtonActive]}
          onPress={() => setShowArchived((prev) => !prev)}
          accessibilityRole="switch"
          accessibilityState={{ checked: showArchived }}
        >
          <View style={styles.toggleIndicator}>
            <View style={[styles.toggleDot, showArchived && styles.toggleDotActive]} />
          </View>
          <Text style={styles.toggleButtonText}>
            {showArchived ? t('tournaments.hideFinished') : t('tournaments.showFinished')}
          </Text>
        </Pressable>
        {visibleTours.map((tour) => {
          const tourTitle = tour.tourName ?? t('tournaments.defaultName');
          const playerLine = (tour.playerNames ?? []).map((name) => name.trim()).filter(Boolean).join(', ');
          const menuOpen = openTourMenuId === tour.id;
          const rawImage = tour.imageThumbKey ?? tour.imageKey ?? '';
          const imageUrl = getTourImageUri(apiBaseUrl, tour.id, rawImage);
          const imageSource = imageUrl ? { uri: imageUrl } : trophyImage;
          const roundsState = roundsByTourId[tour.id];
          const rounds = roundsState?.rounds ?? [];
          const tourStatus = (tour.status ?? '').toLowerCase();
          // Finishing and archiving are both reversible server-side; cancelling is not
          // offered a way back here because it is presented to the user as a delete.
          const canReopen = tourStatus === 'finished' || tourStatus === 'archived';
          const activeRounds = rounds
            .filter((round) => (round.status ?? '').toLowerCase() !== 'cancelled')
            .sort((a, b) => roundPlayDateOrder(a) - roundPlayDateOrder(b));
          const handleNewRound = () => {
            setCurrentTourId(tour.id);
            const debugSuffix = debug ? '?debug=true' : '';
            router.push(`/tour/${tour.id}/round-init${debugSuffix}`);
          };
          const handleScheduleRound = () => {
            setCurrentTourId(tour.id);
            router.push(`/tour/${tour.id}/schedule`);
          };
          const handleOpenRound = (round: TourRoundSummary) => {
            openRoundFromTour(tour.id, round);
          };
          const handleOpenTourEdit = () => {
            setCurrentTourId(tour.id);
            const debugSuffix = debug ? '?debug=true' : '';
            router.push(`/tour/${tour.id}/edit${debugSuffix}`);
          };
          const handleOpenTourSettings = () => {
            setOpenTourMenuId(null);
            handleOpenTourEdit();
          };
          return (
            <View key={tour.id} style={[styles.card, menuOpen && styles.cardMenuOpen]}>
              <View style={styles.cardContent}>
                <View style={styles.cardHeaderRow}>
                  <Pressable
                    style={styles.tourMenuButton}
                    onPress={() => setOpenTourMenuId(menuOpen ? null : tour.id)}
                    accessibilityLabel={t('tournaments.menuLabel')}
                  >
                    <Text style={styles.tourMenuIcon}>⋯</Text>
                  </Pressable>
                  <Pressable style={styles.cardRow} onPress={handleOpenTourEdit} accessibilityLabel={t('tournaments.editCta')}>
                    <Image source={imageSource} style={styles.tourImage} resizeMode="cover" />
                    <View style={styles.cardText}>
                      <View style={styles.cardTitleRow}>
                        <Text style={styles.cardTitle}>{tourTitle}</Text>
                      </View>
                      {playerLine ? <Text style={styles.meta}>{playerLine}</Text> : null}
                      {debug ? <Text style={styles.meta}>ID: {tour.id}</Text> : null}
                      <View style={styles.tourActions}>
                        <Pressable
                          style={styles.editButton}
                          onPress={handleOpenTourEdit}
                          accessibilityLabel={t('tournaments.editCta')}
                        >
                          <Text style={styles.editButtonText}>{t('tournaments.editCta')}</Text>
                        </Pressable>
                      </View>
                    </View>
                  </Pressable>
                </View>
                <View style={styles.roundsBlock}>
                  <View style={styles.roundsHeaderRow}>
                    <Text style={styles.roundsTitle}>
                      {t('tournaments.roundsTitle')}
                      {roundsState ? ` (${activeRounds.length})` : ''}
                    </Text>
                  </View>
                  {roundsState?.loading ? <Text style={styles.roundsLoading}>{t('tournaments.roundsLoading')}</Text> : null}
                  {roundsState?.error ? <Text style={styles.roundsError}>{roundsState.error}</Text> : null}
                  {!roundsState?.loading && !roundsState?.error && activeRounds.length === 0 ? (
                    <Text style={styles.roundsEmpty}>{t('tournaments.roundsEmpty')}</Text>
                  ) : null}
                  <View style={styles.roundsGrid}>
                    {activeRounds.map((round) => {
                      const roundName = round.name ?? round.courseName ?? t('tournaments.roundFallbackName');
                      const roundStatusLabel = formatRoundStatus(round);
                      const normalizedRoundStatus = (round.status ?? '').toLowerCase();
                      const isFinished = normalizedRoundStatus === 'finished';
                      const isDraft = normalizedRoundStatus === 'draft';
                      const roundActionLabel = isFinished
                        ? t('tournaments.roundResults')
                        : isDraft
                          ? t('tournaments.roundStart')
                          : t('tournaments.roundOpen');
                      return (
                        <Pressable
                          key={round.id}
                          style={styles.roundRow}
                          onPress={() => handleOpenRound(round)}
                          accessibilityLabel={roundName}
                        >
                          <View style={styles.roundCard}>
                            <View style={styles.roundHeaderRow}>
                              <View style={styles.roundHeader}>
                                <Text style={styles.roundName}>{roundName}</Text>
                                {roundStatusLabel ? <Text style={styles.roundMeta}>{roundStatusLabel}</Text> : null}
                              </View>
                              <GoffyButton
                                label={roundActionLabel}
                                onPress={() => handleOpenRound(round)}
                                size="compact"
                                tone="dark"
                                oldButtonStyle={styles.roundActionButton}
                                oldDisabledStyle={styles.primaryButtonDisabled}
                                style={styles.roundActionButtonWrap}
                                textStyle={styles.roundActionButtonText}
                              />
                            </View>
                          </View>
                        </Pressable>
                      );
                    })}
                  </View>
                  <GoffyButton
                    label={t('tournaments.newRoundCta')}
                    onPress={handleNewRound}
                    tone="accent"
                    oldButtonStyle={styles.newRoundButton}
                    oldTextStyle={styles.newRoundButtonText}
                    oldDisabledStyle={styles.primaryButtonDisabled}
                    style={styles.newRoundButtonWide}
                  />
                  <GoffyButton
                    label={t('schedule.scheduleRoundCta')}
                    onPress={handleScheduleRound}
                    tone="dark"
                    style={styles.newRoundButtonWide}
                    testID={`schedule-round-${tour.id}`}
                  />
                </View>
              </View>
              {menuOpen ? (
                <View style={styles.tourMenu}>
                  <Pressable style={styles.tourMenuItem} onPress={handleOpenTourSettings}>
                    <Text style={styles.tourMenuText}>{t('tournaments.tourSettings')}</Text>
                  </Pressable>
                  <Pressable style={styles.tourMenuItem} onPress={() => void handleOpenShareModal(tour)}>
                    <Text style={styles.tourMenuText}>{t('tournaments.shareCta')}</Text>
                  </Pressable>
                  <Pressable
                    style={styles.tourMenuItem}
                    onPress={() => {
                      setOpenTourMenuId(null);
                      router.push(`/tour/${tour.id}/leaderboard`);
                    }}
                  >
                    <Text style={styles.tourMenuText}>{t('tourLeaderboard.title')}</Text>
                  </Pressable>
                  <Pressable
                    style={styles.tourMenuItem}
                    onPress={() => {
                      setOpenTourMenuId(null);
                      router.push(`/tour/${tour.id}/preview`);
                    }}
                  >
                    <Text style={styles.tourMenuText}>{t('tourPreview.title')}</Text>
                  </Pressable>
                  {canReopen ? (
                    <Pressable style={styles.tourMenuItem} onPress={() => updateTourStatus(tour.id, 'active')}>
                      <Text style={styles.tourMenuText}>{t('tournaments.statusReopen')}</Text>
                    </Pressable>
                  ) : (
                    <Pressable
                      style={styles.tourMenuItem}
                      onPress={() => {
                        setOpenTourMenuId(null);
                        // Finishing awards the tour-long game points, so confirm first.
                        Alert.alert(
                          t('tournaments.statusFinish'),
                          t('tournaments.statusFinishConfirm'),
                          [
                            { text: t('tournaments.createCancel'), style: 'cancel' },
                            {
                              text: t('tournaments.statusFinish'),
                              onPress: () => updateTourStatus(tour.id, 'finished'),
                            },
                          ]
                        );
                      }}
                    >
                      <Text style={styles.tourMenuText}>{t('tournaments.statusFinish')}</Text>
                    </Pressable>
                  )}
                  <Pressable style={styles.tourMenuItem} onPress={() => updateTourStatus(tour.id, 'archived')}>
                    <Text style={styles.tourMenuText}>{t('tournaments.statusArchive')}</Text>
                  </Pressable>
                  <Pressable style={styles.tourMenuItem} onPress={() => updateTourStatus(tour.id, 'paused')}>
                    <Text style={styles.tourMenuText}>{t('tournaments.statusSleep')}</Text>
                  </Pressable>
                  <Pressable
                    style={styles.tourMenuItem}
                    onPress={() => {
                      setOpenTourMenuId(null);
                      Alert.alert(
                        t('tournaments.deleteConfirmTitle'),
                        t('tournaments.deleteConfirmMessage', { name: tour.tourName ?? t('tournaments.defaultName') }),
                        [
                          { text: t('tournaments.deleteConfirmKeep'), style: 'cancel' },
                          {
                            text: t('tournaments.deleteConfirmCta'),
                            style: 'destructive',
                            onPress: () => updateTourStatus(tour.id, 'cancelled'),
                          },
                        ]
                      );
                    }}
                  >
                    <Text style={[styles.tourMenuText, styles.tourMenuTextDanger]}>{t('tournaments.statusDelete')}</Text>
                  </Pressable>
                </View>
              ) : null}
            </View>
          );
        })}
        {!visibleTours.length && !loading && !error ? <Text style={styles.subtitle}>{t('tournaments.noTours')}</Text> : null}
        <View>
          <GoffyButton
            label={tourStarting ? t('tournaments.creating') : t('tournaments.createCta')}
            onPress={() => setCreateModalOpen(true)}
            disabled={tourStarting}
            size="compact"
            tone="accent"
            oldButtonStyle={styles.primaryButton}
            oldTextStyle={styles.primaryText}
            oldDisabledStyle={styles.primaryButtonDisabled}
            style={styles.newTourButtonImageMode}
          />
        </View>
      </ScrollView>
      {/* Nothing is persisted until the user confirms a name here. */}
      <CreateTourModal
        visible={createModalOpen}
        creating={tourStarting}
        error={createError}
        onCreate={(name) => void createTour(name)}
        onCancel={() => setCreateModalOpen(false)}
      />
      <Modal
        transparent
        visible={shareModalOpen}
        animationType="fade"
        onRequestClose={() => setShareModalOpen(false)}
      >
        <Pressable style={styles.shareOverlay} onPress={() => setShareModalOpen(false)}>
          <Pressable style={styles.shareCard} onPress={() => null}>
            <Text style={styles.shareTitle}>
              {t('tournaments.shareTitle', { name: shareModalTourName ?? t('tournaments.defaultName') })}
            </Text>
            <View style={styles.shareCodeCard}>
              <Text style={styles.shareCodeLabel}>{t('players.joinWithCode')}</Text>
              <Text style={styles.shareCodeValue}>{shareJoinCode || '--- ---'}</Text>
              {shareModalLoading ? (
                <ActivityIndicator size="small" color={appColors.accent.primary} />
              ) : null}
              {shareModalLink ? (
                <View style={styles.shareQrWrapper}>
                  <QRCode value={shareModalLink} size={220} color="#000000" backgroundColor="#ffffff" />
                </View>
              ) : null}
            </View>
            {shareModalError ? <Text style={styles.error}>{shareModalError}</Text> : null}
            <View style={styles.shareActions}>
              <GoffyButton
                label={t('tournaments.shareAction')}
                onPress={() => void handleShareInviteLink()}
                disabled={!shareModalLink || shareModalLoading}
                tone="accent"
                size="compact"
                style={styles.shareActionButton}
              />
              <GoffyButton
                label={t('tournaments.shareClose')}
                onPress={() => setShareModalOpen(false)}
                tone="dark"
                size="compact"
                style={styles.shareActionButton}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
      {/* Finishing a single round locks its scores in, so confirm first. */}
      <Modal
        transparent
        visible={!!finishConfirmRound}
        animationType="fade"
        onRequestClose={() => setFinishConfirmRound(null)}
      >
        <Pressable style={styles.shareOverlay} onPress={() => setFinishConfirmRound(null)}>
          <Pressable style={styles.shareCard} onPress={() => null}>
            <Text style={styles.shareTitle}>{t('tournaments.statusFinishRound')}</Text>
            <Text style={styles.deleteConfirmMessage}>{t('tournaments.statusFinishRoundConfirm')}</Text>
            <View style={styles.shareActions}>
              <GoffyButton
                label={t('tournaments.createCancel')}
                onPress={() => setFinishConfirmRound(null)}
                tone="accent"
                size="compact"
                style={styles.shareActionButton}
              />
              <GoffyButton
                label={t('tournaments.statusFinishRound')}
                onPress={() => {
                  if (finishConfirmRound) {
                    finishRound(finishConfirmRound.roundId);
                  }
                  setFinishConfirmRound(null);
                }}
                tone="dark"
                size="compact"
                style={styles.shareActionButton}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal
        transparent
        visible={!!deleteConfirmRound}
        animationType="fade"
        onRequestClose={() => setDeleteConfirmRound(null)}
      >
        <Pressable style={styles.shareOverlay} onPress={() => setDeleteConfirmRound(null)}>
          <Pressable style={styles.shareCard} onPress={() => null}>
            <Text style={styles.shareTitle}>{t('tournaments.deleteRoundConfirmTitle')}</Text>
            <Text style={styles.deleteConfirmMessage}>
              {t('tournaments.deleteRoundConfirmMessage', {
                name: deleteConfirmRound?.name ?? t('tournaments.roundFallbackName'),
              })}
            </Text>
            <View style={styles.shareActions}>
              <GoffyButton
                label={t('tournaments.deleteConfirmKeep')}
                onPress={() => setDeleteConfirmRound(null)}
                tone="accent"
                size="compact"
                style={styles.shareActionButton}
              />
              <GoffyButton
                label={t('tournaments.deleteConfirmCta')}
                onPress={() => {
                  if (deleteConfirmRound) {
                    updateTourStatus(deleteConfirmRound.tourId, 'cancelled');
                  }
                  setDeleteConfirmRound(null);
                }}
                tone="dark"
                size="compact"
                style={styles.shareActionButton}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: 20,
    gap: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appColors.background.app,
  },
  heroImage: {
    width: 140,
    height: 140,
  },
  title: {
    fontSize: 26,
    fontWeight: '800',
    color: appColors.text.heading,
  },
  subtitle: {
    color: appColors.text.secondary,
    textAlign: 'center',
  },
  smallNote: {
    color: appColors.text.secondary,
  },
  card: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    gap: 8,
    flexDirection: 'column',
    alignItems: 'stretch',
    position: 'relative',
    zIndex: 1,
  },
  cardMenuOpen: {
    borderColor: '#A1D493',
    zIndex: 10,
    elevation: 10,
  },
  cardContent: {
    width: '100%',
    gap: 8,
  },
  cardHeaderRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  cardRow: {
    flex: 1,
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  cardText: {
    flex: 1,
    gap: 6,
  },
  tourImage: {
    width: 100,
    height: 100,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  tourImagePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  tourMenuButton: {
    width: 36,
    height: 36,
    borderRadius: 12,
    backgroundColor: appColors.background.panelAlt,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  tourMenuIcon: {
    color: appColors.text.secondary,
    fontSize: 20,
    marginTop: -4,
  },
  tourMenu: {
    position: 'absolute',
    top: 48,
    left: 12,
    backgroundColor: appColors.surface.elevated,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.36)',
    paddingVertical: 8,
    minWidth: 140,
    zIndex: 20,
    elevation: 20,
  },
  tourMenuItem: {
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  tourMenuText: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
  },
  tourMenuTextDanger: {
    color: '#ffb3b3',
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  meta: {
    color: appColors.accent.primary,
  },
  tourActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 6,
  },
  editButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.4)',
    backgroundColor: 'rgba(47,138,78,0.12)',
  },
  editButtonText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  roundsBlock: {
    width: '100%',
    alignSelf: 'stretch',
    gap: 8,
    marginTop: 6,
  },
  roundsHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  roundsTitle: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
  },
  roundsLoading: {
    color: '#A1D493',
    fontSize: 12,
  },
  roundsEmpty: {
    color: '#A1D493',
    fontSize: 12,
  },
  roundsError: {
    color: '#ff9fa6',
    fontSize: 12,
    fontWeight: '600',
  },
  roundsGrid: {
    gap: 8,
  },
  roundRow: {
    width: '100%',
    alignSelf: 'stretch',
  },
  roundCard: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    gap: 8,
  },
  roundHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  roundHeader: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  roundName: {
    color: '#C8C8B0',
    fontSize: 14,
    fontWeight: '700',
  },
  roundMeta: {
    color: appColors.accent.primary,
    fontSize: 12,
  },
  roundActionButtonWrap: {
    alignSelf: 'center',
    marginLeft: 'auto',
  },
  roundActionButton: {
    paddingHorizontal: 10,
    height: 36,
  },
  roundActionButtonText: {
    color: appColors.text.secondary,
    fontSize: 14,
    fontWeight: '700',
  },
  hiddenRoundActionButtonWrap: {
    width: 70,
    marginLeft: 'auto',
  },
  roundMenuAnchor: {
    position: 'relative',
    marginRight: 10,
    zIndex: 30,
  },
  roundMenuButton: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: appColors.background.panelAlt,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  roundMenu: {
    position: 'absolute',
    top: 38,
    left: 0,
    backgroundColor: appColors.surface.elevated,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.36)',
    paddingVertical: 8,
    minWidth: 160,
    zIndex: 30,
    elevation: 20,
  },
  // Later siblings paint above earlier ones, so the row holding the open menu
  // (and the whole single-rounds block) has to be raised over what follows.
  roundRowMenuOpen: {
    zIndex: 30,
    elevation: 20,
  },
  roundsBlockMenuOpen: {
    zIndex: 40,
    elevation: 20,
  },
  newRoundButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  newRoundButtonText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  newRoundButtonWide: {
    alignSelf: 'stretch',
    width: '100%',
  },
  provisioningRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
  },
  provisioningRetryRow: {
    alignSelf: 'stretch',
    gap: 8,
  },
  retryButton: {
    alignSelf: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  retryButtonText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '700',
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    alignSelf: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#0C2510',
  },
  toggleButtonActive: {
    borderColor: '#A1D493',
    backgroundColor: '#2d6625',
  },
  toggleButtonText: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.8,
  },
  toggleIndicator: {
    width: 34,
    height: 20,
    borderRadius: 10,
    backgroundColor: appColors.surface.alt,
    borderWidth: 1,
    borderColor: '#A1D493',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  toggleDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#A1D493',
    alignSelf: 'flex-start',
  },
  toggleDotActive: {
    backgroundColor: '#A1D493',
    alignSelf: 'flex-end',
  },
  primaryButton: {
    marginTop: 8,
    paddingVertical: 8,
  },
  primaryText: {
    color: appColors.text.primary,
    fontWeight: '800',
    fontSize: 16,
    textAlign: 'center',
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  newTourButtonImageMode: {
    marginTop: 8,
    width: 124,
    height: 34,
  },
  temporaryButtonWrap: {
    alignItems: 'flex-end',
  },
  secondaryButtonImageMode: {
    width: 124,
    height: 34,
  },
  secondaryButtonImageText: {
    fontSize: 14,
  },
  shareButtonImageMode: {
    width: 180,
    height: 56,
  },
  membersCard: {
    width: '100%',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panelAlt,
    padding: 12,
    gap: 10,
  },
  membersTitle: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  membersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  memberChip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#A1D493',
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#07200D',
  },
  memberChipSelected: {
    borderColor: '#A1D493',
    backgroundColor: 'rgba(47,138,78,0.16)',
  },
  memberChipLocked: {
    borderColor: '#A1D493',
    backgroundColor: 'rgba(42,119,66,0.18)',
  },
  memberChipText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '700',
  },
  memberChipTextSelected: {
    color: '#323835',
  },
  memberChipTextLocked: {
    color: '#2d6b66',
  },
  temporaryRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  secondaryButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#C8C8B0',
    paddingHorizontal: 10,
    paddingVertical: 8,
    maxWidth: '100%',
  },
  secondaryButtonText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
  },
  shareOverlay: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  shareCard: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: '#07200D',
    padding: 18,
    gap: 12,
  },
  shareTitle: {
    color: appColors.text.primary,
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  shareCodeCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#04210D',
    padding: 16,
    gap: 10,
    alignItems: 'center',
  },
  shareCodeLabel: {
    color: '#8FA08B',
    fontSize: 12,
    letterSpacing: 3,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  shareCodeValue: {
    color: '#AEE79A',
    fontSize: 48,
    letterSpacing: 6,
    fontWeight: '700',
    textAlign: 'center',
  },
  shareQrWrapper: {
    alignSelf: 'center',
    borderRadius: 12,
    backgroundColor: '#f6f8f6',
    padding: 10,
  },
  shareLinkText: {
    color: appColors.text.primary,
    fontSize: 12,
    textAlign: 'center',
  },
  shareActions: {
    flexDirection: 'column',
    gap: 10,
    justifyContent: 'flex-start',
  },
  shareActionButton: {
    width: '100%',
  },
  deleteConfirmMessage: {
    color: appColors.text.secondary,
    fontSize: 14,
    textAlign: 'center',
  },
  error: {
    color: '#ff7b7b',
    marginTop: 4,
  },
  debugUser: {
    alignSelf: 'flex-end',
    color: appColors.text.muted,
    fontSize: 12,
  },
});

function generateId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}
